// src/utils/questionnaireQuestions/renderStep.tsx
/* ------------------------------------------------------------------ */
/* renderStep – selects & renders the current questionnaire “step”     */
/* ------------------------------------------------------------------ */
/**
 * Helper used by **DynamicQuestionnaireForm**.
 *
 * @param {Array<object>} steps              Output of `buildSteps()`
 * @param {number}        currentStep        Zero-based index of the step to render
 * @param {object}        formData           `{ [questionId]: answer }`
 * @param {Function}      handleChange       `onChange` handler for <input/>, <select/>, …
 * @param {Function}      goNext             Callback that advances to the next step
 * @param {Array<object>} filteredQuestions  Full question list **after** conditional filtering
 *
 * @returns {JSX.Element|null}
 *
 * **Step types**
 *  - `single` → `<QuestionStep>`      (one question)
 *  - `group`  → `<GroupedQuestionStep>` (multiple questions with the same `group` label)
 *  - `review` → `<ReviewStep>`        (read-only summary before submission)
 */
import GroupedQuestionStep from "../../components/form/GroupedQuestionStep";
import QuestionStep from "../../components/form/QuestionStep";
import ReviewStep from "../../components/form/ReviewStep";
import SectionHeader from "../../components/questionnaire/SectionHeader";

const getStepGroupId = (step) =>
  step?.type === "single"
    ? step.question?.group
    : step?.type === "group"
    ? step.questions?.[0]?.group
    : null;
/* ------------------------------------------------------------------ */
/* Render                                                             */
/* ------------------------------------------------------------------ */
export const renderStep = (
  steps,
  currentStep,
  formData,
  handleChange,
  goNext,
  filteredQuestions,
  translationForm
) => {
  const step = steps[currentStep];
  const groupId = getStepGroupId(step);
  switch (step.type) {
    case "single":
      return (
        <div className="space-y-3">
          <SectionHeader groupId={groupId} translationForm={translationForm} />
          <QuestionStep
            question={step.question}
            value={formData[step.question.id]}
            onChange={handleChange}
            autoFocus
            onEnter={goNext}
            translationForm={translationForm}
          />
        </div>
      );

    case "group":
      return (
        <div className="space-y-3">
          <SectionHeader groupId={groupId} translationForm={translationForm} />
          <GroupedQuestionStep
            questions={step.questions}
            formData={formData}
            onChange={handleChange}
            onEnter={goNext}
            translationForm={translationForm}
          />
        </div>
      );

    case "review":
      return (
        <ReviewStep
          questions={filteredQuestions}
          formData={formData}
          translationForm={translationForm}
        />
      );

    default:
      return null;
  }
};
